import React from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle, Color } from '@tiptap/extension-text-style';
import {
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, AlignLeft, AlignCenter, AlignRight,
  Link as LinkIcon, Undo, Redo, Type, Eraser
} from 'lucide-react';

const MenuButton = ({ onClick, isActive, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded hover:bg-gray-200 transition-colors ${isActive ? 'bg-blue-100 text-blue-700' : 'text-gray-600'}`}
  >
    {children}
  </button>
);

export default function RichTextEditor({ value, content, onChange, placeholder }) {
  // Back-compat: aceptar tanto `value` como `content` como prop del contenido inicial
  const incomingValue = value !== undefined ? value : content;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextStyle,
      Color,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: incomingValue || '',
    onUpdate: ({ editor }) => {
      if (typeof onChange === 'function') {
        onChange(editor.getHTML());
      }
    },
  });

  React.useEffect(() => {
    if (editor && incomingValue !== editor.getHTML()) {
      editor.commands.setContent(incomingValue || '');
    }
  }, [incomingValue, editor]);

  if (!editor) return null;

  const addLink = () => {
    const url = window.prompt('URL:');
    if (url) {
      editor.chain().focus().setLink({ href: url }).run();
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden bg-white">
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b bg-gray-50">
        <MenuButton onClick={() => editor.chain().focus().toggleBold().run()} isActive={editor.isActive('bold')} title="Negrita">
          <Bold className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleItalic().run()} isActive={editor.isActive('italic')} title="Cursiva">
          <Italic className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleUnderline().run()} isActive={editor.isActive('underline')} title="Subrayado">
          <UnderlineIcon className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleStrike().run()} isActive={editor.isActive('strike')} title="Tachado">
          <Strikethrough className="h-4 w-4" />
        </MenuButton>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <MenuButton onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} isActive={editor.isActive('heading', { level: 2 })} title="Titulo">
          <Type className="h-4 w-4" />
        </MenuButton>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <MenuButton onClick={() => editor.chain().focus().toggleBulletList().run()} isActive={editor.isActive('bulletList')} title="Lista">
          <List className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().toggleOrderedList().run()} isActive={editor.isActive('orderedList')} title="Lista numerada">
          <ListOrdered className="h-4 w-4" />
        </MenuButton>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('left').run()} isActive={editor.isActive({ textAlign: 'left' })} title="Izquierda">
          <AlignLeft className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('center').run()} isActive={editor.isActive({ textAlign: 'center' })} title="Centrar">
          <AlignCenter className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().setTextAlign('right').run()} isActive={editor.isActive({ textAlign: 'right' })} title="Derecha">
          <AlignRight className="h-4 w-4" />
        </MenuButton>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <MenuButton onClick={addLink} isActive={editor.isActive('link')} title="Enlace">
          <LinkIcon className="h-4 w-4" />
        </MenuButton>
        <div className="w-px h-5 bg-gray-300 mx-1" />
        <MenuButton onClick={() => editor.chain().focus().undo().run()} title="Deshacer">
          <Undo className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().redo().run()} title="Rehacer">
          <Redo className="h-4 w-4" />
        </MenuButton>
        <MenuButton onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()} title="Limpiar formato">
          <Eraser className="h-4 w-4" />
        </MenuButton>
        <div className="ml-1">
          <input type="color" className="w-6 h-6 rounded cursor-pointer border border-gray-300" title="Color de texto"
            onInput={(e) => editor.chain().focus().setColor(e.target.value).run()}
            value={editor.getAttributes('textStyle').color || '#000000'} />
        </div>
      </div>
      <EditorContent editor={editor}
        className="prose prose-sm max-w-none px-4 py-3 min-h-[280px] max-h-[50vh] overflow-y-auto focus:outline-none [&_.ProseMirror]:outline-none [&_.ProseMirror]:min-h-[250px]" />
    </div>
  );
}
